import {
  filterAdvanced,
  filterSearch,
  orFilter,
} from "@west-tennessee-consulting/supabase/postgrest-filter";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { ContactFilterCriteria } from "./contacts-types";
import {
  RateLimitedBulkActionExecutor,
  type BulkActionProgressItem,
  type BulkActionProgress,
  type FilterBasedBulkActionParams,
} from "@/lib/hooks/api/shared/bulk-filter-types";

export { RateLimitedBulkActionExecutor };
export type { ContactFilterCriteria, BulkActionProgressItem, BulkActionProgress };
export type { FilterBasedBulkActionParams };

// Builds a Supabase query matching the same filters as useContactsPaginated
export function buildContactFilterQuery(
  supabase: ReturnType<typeof getSupabase>,
  filterCriteria: ContactFilterCriteria
) {
  let query = supabase.from("contacts").select("*");

  if (filterCriteria.status) {
    query = query.eq("status", filterCriteria.status);
  }
  if (filterCriteria.contactType) {
    query = query.eq("contact_type", filterCriteria.contactType);
  }
  if (filterCriteria.isActive !== undefined) {
    query = query.eq("is_active", filterCriteria.isActive);
  }
  if (filterCriteria.company_name) {
    query = query.eq("company_name", filterCriteria.company_name);
  }
  if (filterCriteria.exclude_id) {
    query = query.neq("id", filterCriteria.exclude_id);
  }

  const knownFields = [
    "status",
    "contactType",
    "isActive",
    "company_name",
    "exclude_id",
    "nameSearch",
    "companySearch",
    "emailSearch",
    "search",
    "advancedFilters",
  ];
  (Object.entries(filterCriteria) as [string, unknown][]).forEach(([key, value]) => {
    if (!knownFields.includes(key) && value !== undefined && value !== null && value !== "") {
      if (Array.isArray(value)) {
        query = query.contains(key, value as string[]);
      } else if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
      ) {
        query = query.eq(key, value);
      }
    }
  });

  const specificSearchFilter = orFilter([
    filterSearch("name", filterCriteria.nameSearch ?? ""),
    filterSearch("company_name", filterCriteria.companySearch ?? ""),
    filterSearch("email", filterCriteria.emailSearch ?? ""),
    ...(filterCriteria.search
      ? [
          filterSearch("name", filterCriteria.search),
          filterSearch("email", filterCriteria.search),
          filterSearch("company_name", filterCriteria.search),
        ]
      : []),
  ]);
  if (specificSearchFilter) {
    query = query.or(specificSearchFilter);
  }

  if (filterCriteria.advancedFilters && filterCriteria.advancedFilters.length > 0) {
    const advancedFilter = orFilter(
      filterCriteria.advancedFilters.map(({ field, operator, value }) =>
        filterAdvanced(field, operator, value)
      )
    );
    if (advancedFilter) {
      query = query.or(advancedFilter);
    }
  }

  return query.order("created_at", { ascending: false });
}

// Fetches all contact IDs matching filter criteria with pagination
export async function getFilteredContactIds(
  filterCriteria: ContactFilterCriteria
): Promise<Array<{ id: string; name: string; email?: string }>> {
  const supabase = getSupabase();
  const pageSize = 1000;
  let allContacts: Array<{ id: string; name: string; email?: string }> = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const query = buildContactFilterQuery(supabase, filterCriteria);
    const { data, error } = await query.range(from, to);

    if (error) {
      console.error("Error fetching filtered contact IDs:", error);
      throw new Error(error.message);
    }

    if (!data || data.length === 0) {
      hasMore = false;
    } else {
      const pageContacts = data.map((contact) => ({
        id: contact.id,
        name: contact.name,
        email: contact.email ?? undefined,
      }));
      allContacts = allContacts.concat(pageContacts);
      if (data.length < pageSize) {
        hasMore = false;
      }
      page++;
    }
  }

  return allContacts;
}
